import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { signInWithApple, signInWithGoogle } from "@/lib/auth";
import { isAndroid } from "@/lib/nativeAuth";
import { trackEvent } from "@/lib/analytics";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { SilhouetteModel, type Gender } from "./SilhouetteModel";
import {
  HEIGHT_OPTIONS_BY_GENDER,
  BODY_SHAPE_OPTIONS_BY_GENDER,
  BODY_SIZE_OPTIONS_WOMEN,
  BODY_BUILD_OPTIONS_MEN,
  TOP_SIZE_OPTIONS,
} from "@/lib/profileFieldOptions";

type Verdict = "true_to_size" | "runs_small" | "runs_large";
type BrandFit = { brand: string; verdict: Verdict; category: "tops" | "bottoms" };

type Answers = {
  gender?: Gender;
  height?: "short" | "average_short" | "average_tall" | "tall";
  bodyShape?: string;
  bodySize?: string;
  skinTone?: string;
  hair?: string;
  sizeTopLetter?: string;
  sizeTopUkNumber?: number;
  sizeTrouserWaist?: number;
  sizeUkNumber?: number;
  sizeShoe?: number;
  sizeShoeMens?: number;
  brandFitTops?: BrandFit[];
  brandFitBottoms?: BrandFit[];
  budgetTop?: number;
  budgetBottom?: number;
  budgetShoes?: number;
};

const STORAGE_KEY = "l2f_onboarding_progress";
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

// ───────── Design tokens (F35) ─────────
const PAGE_BG = "#E8E0D5";
const CARD = "#FFFCF8";
const CARD_BORDER = "#E8E2D9";
const PILL_BORDER = "#E0D8D0";
const ACTIVE_BG = "#FAECE7";
const CORAL = "#C84B2F";
const INK = "#2C2820";
const INK_ACTIVE = "#712B13";
const MUTED = "#9B958E";
const SUBTLE = "#6B6560";
const DISABLED_BG = "#D3D1C7";
const DISABLED_INPUT_BG = "#F1EFE8";

type ScreenKey =
  | "gender"
  | "auth"
  | "hook"
  | "height"
  | "body_shape"
  | "body_size"
  | "body_build"
  | "skin"
  | "hair"
  | "sizes"
  | "budget"
  | "reveal"
  | "brand_combined";

function flowFor(gender?: Gender): ScreenKey[] {
  if (gender === "men") {
    return [
      "gender",
      "auth",
      "hook",
      "height",
      "body_build",
      "skin",
      "hair",
      "sizes",
      "budget",
      "reveal",
      "brand_combined",
    ];
  }
  return [
    "gender",
    "auth",
    "hook",
    "height",
    "body_shape",
    "body_size",
    "skin",
    "hair",
    "sizes",
    "budget",
    "reveal",
    "brand_combined",
  ];
}

// ───────── helpers ─────────

function loadProgress(): { gender?: Gender; answers: Answers; screen: ScreenKey } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > STORAGE_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { gender: parsed.gender, answers: parsed.answers ?? {}, screen: parsed.screen };
  } catch {
    return null;
  }
}

function saveProgress(gender: Gender | undefined, answers: Answers, screen: ScreenKey) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ gender, answers, screen, ts: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

function clearProgress() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ───────── primitives ─────────

function Eyebrow({
  children,
  tone = "coral",
}: {
  children: React.ReactNode;
  tone?: "coral" | "muted";
}) {
  return (
    <p
      className="l2f-type-section-header"
      style={{
        color: tone === "coral" ? CORAL : MUTED,
        marginBottom: 4,
      }}
    >
      {children}
    </p>
  );
}

// `size` retained for backwards-compat; typography is now token-driven (D-2a).
function Headline({ children, size: _size = 22 }: { children: React.ReactNode; size?: number }) {
  void _size;
  return (
    <h2
      className="l2f-type-h1"
      style={{
        color: INK,
        marginTop: 4,
      }}
    >
      {children}
    </h2>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="l2f-type-caption" style={{ color: MUTED, marginTop: 4 }}>{children}</p>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="l2f-type-secondary" style={{ color: SUBTLE, marginTop: 8 }}>
      {children}
    </p>
  );
}

function VerticalPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        triggerHaptic("selection");
        onClick();
      }}
      style={{
        display: "block",
        width: "100%",
        minHeight: 44,
        padding: "12px 14px",
        background: selected ? ACTIVE_BG : CARD,
        border: selected ? `1.5px solid ${CORAL}` : `1px solid ${PILL_BORDER}`,
        borderRadius: 20,
        fontFamily: "var(--font-body)",
        fontSize: 15,
        lineHeight: 1.4,
        color: selected ? INK_ACTIVE : INK,
        fontWeight: selected ? 500 : 400,
        textAlign: "center",
        marginBottom: 8,
        boxSizing: "border-box",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function SmallPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        triggerHaptic("selection");
        onClick();
      }}
      style={{
        display: "inline-block",
        padding: "6px 11px",
        background: selected ? ACTIVE_BG : CARD,
        border: selected ? `1.5px solid ${CORAL}` : `1px solid ${PILL_BORDER}`,
        borderRadius: 14,
        fontFamily: "var(--font-body)",
        fontSize: 12,
        lineHeight: 1.4,
        color: selected ? INK_ACTIVE : INK,
        fontWeight: selected ? 500 : 400,
        margin: "0 4px 4px 0",
        whiteSpace: "nowrap",
        cursor: "pointer",
        boxSizing: "border-box",
      }}
    >
      {label}
    </button>
  );
}

function SectionCard({
  eyebrow,
  children,
}: {
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: CARD,
        border: `0.5px solid ${CARD_BORDER}`,
        borderRadius: 16,
        padding: 14,
        marginBottom: 10,
      }}
    >
      {eyebrow && (
        <p
          className="l2f-type-section-header"
          style={{
            color: MUTED,
            marginBottom: 8,
          }}
        >
          {eyebrow}
        </p>
      )}
      {children}
    </div>
  );
}

function ModelCard({
  answers,
  gender,
  eyebrowSuffix,
}: {
  answers: Answers;
  gender: Gender | undefined;
  eyebrowSuffix?: string;
}) {
  return (
    <div
      style={{
        background: CARD,
        border: `0.5px solid ${CARD_BORDER}`,
        borderRadius: 16,
        padding: 10,
        marginBottom: 14,
      }}
    >
      <p
        className="l2f-type-section-header"
        style={{
          color: MUTED,
          marginBottom: 4,
          textAlign: "center",
        }}
      >
        {eyebrowSuffix ? `YOUR MODEL · ${eyebrowSuffix}` : "YOUR MODEL"}
      </p>
      <div
        style={{
          height: 104,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ height: "100%", width: "auto" }}>
          <SilhouetteModel
            gender={gender ?? null}
            height={answers.height ?? null}
            bodyShape={answers.bodyShape ?? null}
            bodySize={answers.bodySize ?? null}
            skinTone={answers.skinTone ?? null}
            hair={answers.hair ?? null}
            className="h-full"
          />
        </div>
      </div>
    </div>
  );
}

// ───────── main component ─────────

export function GrwmOnboarding({
  session,
  onComplete,
  onAbandon,
}: {
  session: { user: { id: string } } | null;
  onComplete: (data: {
    typicalBudget: number;
    budgetTop: number;
    budgetBottom: number;
    budgetShoes: number;
  }) => void;
  onAbandon?: () => void;
}) {
  const [gender, setGender] = useState<Gender | undefined>(undefined);
  const [answers, setAnswers] = useState<Answers>({});
  const [screenKey, setScreenKey] = useState<ScreenKey>("gender");
  const [saving, setSaving] = useState(false);

  // Analytics counters
  const startedAtRef = useRef<number | null>(null);
  const completedCountRef = useRef(0);
  const skippedCountRef = useRef(0);
  const lastViewedRef = useRef<ScreenKey | null>(null);

  const restoredRef = useRef(false);
  useEffect(() => {
    const restored = loadProgress();
    if (restored) {
      setGender(restored.gender);
      setAnswers(restored.answers);
      setScreenKey(restored.screen);
    } else {
      startedAtRef.current = Date.now();
      trackEvent("grwm_started", { gender_known: false });
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screenKey === "auth" && session) {
      setScreenKey("hook");
    }
  }, [session, screenKey]);

  useEffect(() => {
    if (!restoredRef.current) return;
    saveProgress(gender, answers, screenKey);
  }, [gender, answers, screenKey]);

  const flow = useMemo(() => flowFor(gender), [gender]);
  const screenIdx = Math.max(0, flow.indexOf(screenKey));

  // Step counter excludes auth so numbering doesn't jump for signed-in users.
  const displayableFlow = useMemo<ScreenKey[]>(
    () => flow.filter((s): s is ScreenKey => s !== "auth"),
    [flow],
  );
  const stepNumber = Math.max(1, displayableFlow.indexOf(screenKey) + 1);
  const totalSteps = displayableFlow.length;

  // Fire step_viewed on every navigation.
  useEffect(() => {
    if (!restoredRef.current) return;
    if (lastViewedRef.current === screenKey) return;
    lastViewedRef.current = screenKey;
    trackEvent("grwm_step_viewed", {
      step: screenKey,
      step_number: stepNumber,
      total_steps: totalSteps,
    });
  }, [screenKey, stepNumber, totalSteps]);

  const goNext = () => {
    const next = flow[screenIdx + 1];
    if (next) setScreenKey(next);
  };
  const goBack = () => {
    const prev = flow[screenIdx - 1];
    if (prev) setScreenKey(prev);
  };

  const setAns = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }));

  const handleContinue = (value?: unknown) => {
    triggerHaptic("success");
    completedCountRef.current += 1;
    trackEvent("grwm_step_completed", { step: screenKey, value: value ?? null });
    goNext();
  };

  const handleSkip = () => {
    triggerHaptic("selection");
    skippedCountRef.current += 1;
    trackEvent("grwm_step_skipped", { step: screenKey });
    goBack();
  };

  const handleClose = () => {
    triggerHaptic("selection");
    trackEvent("grwm_abandoned", {
      last_step: screenKey,
      completed_count: completedCountRef.current,
    });
    onAbandon?.();
  };

  const finishOnboarding = async (extra?: Partial<Answers>) => {
    if (!session?.user.id) return;
    setSaving(true);
    const userId = session.user.id;

    const finalAnswers: Answers = { ...answers, ...extra };

    const sizeGender =
      gender === "men" ? "men" : gender === "non-binary" ? "non-binary" : "women";

    const top = finalAnswers.budgetTop ?? -1;
    const bot = finalAnswers.budgetBottom ?? -1;
    const shoes = finalAnswers.budgetShoes ?? -1;
    const positives = [top, bot, shoes].filter((v) => v > 0);
    const typicalBudget =
      positives.length === 0
        ? -1
        : Math.round(positives.reduce((a, b) => a + b, 0) / positives.length);

    const patch: Record<string, unknown> = {
      size_gender: sizeGender,
      model_gender: sizeGender,
      gender: sizeGender,
      model_height: finalAnswers.height ?? null,
      model_body_shape: finalAnswers.bodyShape ?? null,
      model_body_size: finalAnswers.bodySize ?? null,
      model_skin_tone: finalAnswers.skinTone ?? null,
      model_hair: finalAnswers.hair ?? null,
      model_setup_complete: true,
      seen_onboarding: true,
      size_top_letter: finalAnswers.sizeTopLetter ?? null,
      size_top_uk_number:
        finalAnswers.sizeTopUkNumber != null
          ? String(finalAnswers.sizeTopUkNumber)
          : null,
      size_trouser_waist: finalAnswers.sizeTrouserWaist ?? null,
      size_uk_number: finalAnswers.sizeUkNumber ?? null,
      size_shoe: finalAnswers.sizeShoe ?? null,
      size_shoe_mens: finalAnswers.sizeShoeMens ?? null,
      brand_fit_tops: finalAnswers.brandFitTops ?? [],
      brand_fit_bottoms: finalAnswers.brandFitBottoms ?? [],
      budget_top: top,
      budget_bottom: bot,
      budget_shoes: shoes,
      typical_budget: typicalBudget,
      onboarded_at: new Date().toISOString(),
    };

    const { error: saveError } = await (supabase.from("profiles") as any)
      .update(patch)
      .eq("id", userId)
      .select("id")
      .single();

    if (saveError) {
      console.error("Onboarding save failed:", saveError);
      setSaving(false);
      return;
    }

    const totalSeconds = startedAtRef.current
      ? Math.round((Date.now() - startedAtRef.current) / 1000)
      : 0;
    trackEvent("grwm_completed", {
      total_time_seconds: totalSeconds,
      steps_completed: completedCountRef.current,
      steps_skipped: skippedCountRef.current,
    });

    clearProgress();
    setSaving(false);
    onComplete({
      typicalBudget,
      budgetTop: top,
      budgetBottom: bot,
      budgetShoes: shoes,
    });
  };

  const showTopBar = screenKey !== "reveal";
  const skipHidden = new Set<ScreenKey>(["hook", "gender", "auth", "reveal"]);
  const showSkip = !skipHidden.has(screenKey);
  const ctaEnabled = screenKey === "gender" ? !!gender : true;
  const ctaLabel = screenKey === "hook" ? "Get started" : "Continue";

  // Dynamic eyebrow suffix on model card.
  const modelEyebrow = (() => {
    const parts: string[] = [];
    if (answers.bodyShape) parts.push("SHAPE");
    if (answers.skinTone) parts.push("SKIN");
    if (answers.hair) parts.push("HAIR");
    if (!parts.length) return undefined;
    return `${parts.join(" + ")} APPLIED`;
  })();

  // CTA actions vary per step
  const handleCtaClick = () => {
    if (screenKey === "brand_combined") {
      finishOnboarding();
      return;
    }
    if (screenKey === "reveal") {
      goNext();
      return;
    }
    // Capture the answer summary for analytics
    const valueByScreen: Record<string, unknown> = {
      gender,
      height: answers.height,
      body_shape: answers.bodyShape,
      body_size: answers.bodySize,
      body_build: answers.bodyShape,
      skin: answers.skinTone,
      hair: answers.hair,
      sizes: {
        top: answers.sizeTopLetter,
        ukNum: answers.sizeTopUkNumber,
        waist: answers.sizeTrouserWaist,
        bottom: answers.sizeUkNumber,
        shoe: answers.sizeShoe ?? answers.sizeShoeMens,
      },
      budget: {
        top: answers.budgetTop,
        bottom: answers.budgetBottom,
        shoes: answers.budgetShoes,
      },
    };
    handleContinue(valueByScreen[screenKey as string]);
  };

  // Brand combined uses its own skip
  const skipHandler = screenKey === "brand_combined"
    ? () => {
        trackEvent("grwm_step_skipped", { step: screenKey });
        finishOnboarding({ brandFitTops: [], brandFitBottoms: [] });
      }
    : handleSkip;
  const skipLabel = screenKey === "brand_combined" ? "Skip for now" : "Skip — I'll add this later";

  return (
    <div
      style={{
        background: PAGE_BG,
        height: "100dvh",
        minHeight: "100dvh",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* ZONE 1 — TOP BAR */}
      {showTopBar && (
        <div
          style={{
            position: "absolute",
            top: "calc(var(--safe-top, 0px) + 8px)",
            left: 14,
            right: 14,
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <span
                className="l2f-type-section-header"
                style={{ color: MUTED }}
              >
                STEP {stepNumber} OF {totalSteps}
              </span>
              <span
                style={{
                  color: MUTED,
                  fontSize: 12,
                  lineHeight: 1.4,
                  fontFamily: "var(--font-display, 'Instrument Serif', serif)",
                  fontStyle: "italic",
                }}
              >
                Building you
              </span>
            </div>
            <div
              style={{
                background: "rgba(44,40,32,0.12)",
                height: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round((stepNumber / totalSteps) * 100)}%`,
                  height: "100%",
                  background: CORAL,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
          {onAbandon && (
            <button
              onClick={handleClose}
              aria-label="Close onboarding"
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                background: "rgba(255,255,255,0.5)",
                border: "1px solid rgba(0,0,0,0.07)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#4A4540",
                fontSize: 14,
                padding: 0,
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* ZONE 2 — SCROLLABLE CONTENT */}
      <div
        style={{
          position: "absolute",
          top: showTopBar ? "calc(var(--safe-top, 0px) + 70px)" : 0,
          left: 14,
          right: 14,
          bottom: "calc(var(--safe-bottom, 0px) + 116px)",
          overflowY: "auto",
          zIndex: 2,
        }}
      >
        {renderContent()}
      </div>

      {/* ZONE 3 — FOOTER */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "calc(var(--safe-bottom, 0px) + 116px)",
          padding: "24px 14px calc(var(--safe-bottom, 0px) + 14px)",
          background: `linear-gradient(to bottom, rgba(232,224,213,0) 0%, ${PAGE_BG} 30%, ${PAGE_BG} 100%)`,
          zIndex: 4,
          boxSizing: "border-box",
        }}
      >
        {screenKey === "auth" ? null : (
          <>
            <button
              onClick={handleCtaClick}
              disabled={!ctaEnabled || saving}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "12px",
                background: ctaEnabled && !saving ? CORAL : DISABLED_BG,
                color: ctaEnabled && !saving ? "#fff" : MUTED,
                border: "none",
                borderRadius: 22,
                fontFamily: "var(--font-body)",
                fontSize: 15,
                lineHeight: 1.4,
                fontWeight: 500,
                cursor: ctaEnabled && !saving ? "pointer" : "not-allowed",
              }}
            >
              {saving ? "…" : ctaLabel}
            </button>
            {showSkip && (
              <div style={{ marginTop: 8, textAlign: "center" }}>
                <button
                  onClick={skipHandler}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: SUBTLE,
                    fontFamily: "var(--font-body)",
                    fontSize: 12,
                    lineHeight: 1.4,
                    padding: "10px 12px",
                    minHeight: 44,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {skipLabel}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  function renderContent() {
    switch (screenKey) {
      case "gender":
        return (
          <div>
            <Eyebrow>LIKE2FIT</Eyebrow>
            <Headline>Which model fits you best?</Headline>
            <Hint>Required</Hint>
            <div style={{ marginTop: 14 }}>
              {(
                [
                  ["women", "Woman"],
                  ["men", "Man"],
                  ["non-binary", "Non-binary"],
                ] as [Gender, string][]
              ).map(([g, label]) => (
                <VerticalPill
                  key={g}
                  label={label}
                  selected={gender === g}
                  onClick={() => {
                    setGender(g);
                    setAns({});
                  }}
                />
              ))}
            </div>
          </div>
        );

      case "auth":
        return <AuthStep />;

      case "hook":
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "0 20px",
            }}
          >
            <div style={{ width: 84, height: 200, marginTop: 8 }}>
              <SilhouetteModel
                gender={gender ?? null}
                height={null}
                bodyShape={null}
                bodySize={null}
                skinTone={null}
                hair={null}
                className="h-full w-full"
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <Eyebrow>LIKE2FIT · GETTING TO KNOW YOU</Eyebrow>
              <Headline size={24}>Let&apos;s build your model</Headline>
              <Body>
                A few quick questions so every look we generate fits the real
                you. Around 90 seconds.
              </Body>
            </div>
          </div>
        );

      case "height": {
        const g = gender === "men" ? "men" : gender === "non-binary" ? "non-binary" : "women";
        const opts = HEIGHT_OPTIONS_BY_GENDER[g];
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>How tall are you?</Headline>
            <Hint>Helps every look land at the right proportion.</Hint>
            <div style={{ marginTop: 14 }}>
              {opts.map((o) => (
                <VerticalPill
                  key={o.value}
                  label={o.label}
                  selected={answers.height === o.value}
                  onClick={() => setAns({ height: o.value as Answers["height"] })}
                />
              ))}
            </div>
          </div>
        );
      }

      case "body_shape": {
        const isNB = gender === "non-binary";
        const opts = isNB
          ? BODY_SHAPE_OPTIONS_BY_GENDER["non-binary"]
          : BODY_SHAPE_OPTIONS_BY_GENDER.women;
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>What&apos;s your shape?</Headline>
            <div style={{ marginTop: 14 }}>
              {opts.map((o) => (
                <VerticalPill
                  key={o.value}
                  label={o.label}
                  selected={answers.bodyShape === o.value}
                  onClick={() => setAns({ bodyShape: o.value })}
                />
              ))}
            </div>
          </div>
        );
      }

      case "body_size": {
        const opts = BODY_SIZE_OPTIONS_WOMEN;
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>And your build?</Headline>
            <div style={{ marginTop: 14 }}>
              {opts.map((o) => (
                <VerticalPill
                  key={o.value}
                  label={o.label}
                  selected={answers.bodySize === o.value}
                  onClick={() => setAns({ bodySize: o.value })}
                />
              ))}
            </div>
          </div>
        );
      }

      case "body_build": {
        const opts = BODY_BUILD_OPTIONS_MEN;
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>Your build?</Headline>
            <div style={{ marginTop: 14 }}>
              {opts.map((o) => (
                <VerticalPill
                  key={o.value}
                  label={o.label}
                  selected={answers.bodyShape === o.value}
                  onClick={() => setAns({ bodyShape: o.value, bodySize: o.value })}
                />
              ))}
            </div>
          </div>
        );
      }

      case "skin": {
        const swatches: { value: string; hex: string }[] = [
          { value: "light", hex: "#FDDBB4" },
          { value: "light_medium", hex: "#F5C18A" },
          { value: "medium", hex: "#D4956A" },
          { value: "medium_deep", hex: "#B07346" },
          { value: "deep", hex: "#7B4F2E" },
          { value: "very_deep", hex: "#4A2910" },
        ];
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>Pick your skin tone</Headline>
            <Hint>Helps us steer colour suggestions toward what suits you.</Hint>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px 4px",
              }}
            >
              {swatches.map((s) => {
                const active = answers.skinTone === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => {
                      triggerHaptic("selection");
                      setAns({ skinTone: s.value });
                    }}
                    aria-label={s.value}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 22,
                      background: s.hex,
                      border: "none",
                      boxShadow: active ? `0 0 0 1.5px ${CORAL}` : "none",
                      transform: active ? "scale(1.08)" : "scale(1)",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      }

      case "hair": {
        const isMen = gender === "men";
        const isNB = gender === "non-binary";
        const opts = isMen
          ? [
              { value: "short_dark", label: "Short dark" },
              { value: "short_light", label: "Short light" },
              { value: "bald_shaved", label: "Bald / shaved" },
              { value: "natural_afro", label: "Natural / afro" },
              { value: "locs_braids", label: "Locs or braids" },
              { value: "medium_length", label: "Medium length" },
              { value: "curly", label: "Curly" },
            ]
          : isNB
            ? [
                { value: "short_dark", label: "Short dark" },
                { value: "short_light", label: "Short light" },
                { value: "long_dark", label: "Long dark" },
                { value: "long_light", label: "Long light / blonde" },
                { value: "natural_afro", label: "Natural / afro" },
                { value: "medium_wavy", label: "Medium wavy" },
                { value: "locs_braids", label: "Locs or braids" },
                { value: "bald_shaved", label: "Bald / shaved" },
                { value: "curly", label: "Curly" },
              ]
            : [
                { value: "short_dark", label: "Short dark" },
                { value: "short_light", label: "Short light" },
                { value: "long_dark", label: "Long dark" },
                { value: "long_light", label: "Long light / blonde" },
                { value: "natural_afro", label: "Natural / afro" },
                { value: "medium_wavy", label: "Medium wavy" },
                { value: "locs_braids", label: "Locs or braids" },
              ];
        return (
          <div>
            <ModelCard answers={answers} gender={gender} eyebrowSuffix={modelEyebrow} />
            <Eyebrow tone="coral">YOUR MODEL</Eyebrow>
            <Headline>Your hair?</Headline>
            <div style={{ marginTop: 14 }}>
              {opts.map((o) => (
                <VerticalPill
                  key={o.value}
                  label={o.label}
                  selected={answers.hair === o.value}
                  onClick={() => setAns({ hair: o.value })}
                />
              ))}
            </div>
          </div>
        );
      }

      case "sizes":
        return <SizesContent gender={gender} answers={answers} setAns={setAns} />;

      case "budget":
        return <BudgetContent answers={answers} setAns={setAns} />;

      case "reveal":
        return <RevealContent answers={answers} gender={gender ?? null} />;

      case "brand_combined":
        return <BrandCombinedContent gender={gender ?? null} answers={answers} setAns={setAns} />;

      default:
        return null;
    }
  }
}

// ───────── Auth (signup) step ─────────

function AuthStep() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const signupEmail = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      if (!data.session) {
        throw new Error("Account created but session was not established. Please log in.");
      }
    } catch (err: any) {
      setError(err.message ?? "Couldn't sign up");
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    setError(null);
    const r = await signInWithGoogle();
    if (r.error) setError(r.error.message ?? "Google sign-in failed");
  };

  const apple = async () => {
    setError(null);
    const r = await signInWithApple();
    if (r.error) setError(r.error.message ?? "Apple sign-in failed");
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: 44,
    background: CARD,
    border: `1px solid ${PILL_BORDER}`,
    borderRadius: 12,
    padding: "12px 14px",
    fontFamily: "var(--font-body)",
    fontSize: 16,
    lineHeight: 1.5,
    color: INK,
    boxSizing: "border-box",
  };

  return (
    <div>
      <Eyebrow>LIKE2FIT</Eyebrow>
      <Headline>Somewhere to save your model</Headline>
      <Hint>Takes 10 seconds. Free forever.</Hint>

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          style={inputStyle}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          style={inputStyle}
        />
        <button
          onClick={signupEmail}
          disabled={!email || !password || loading}
          style={{
            width: "100%",
            minHeight: 44,
            padding: 12,
            background: !email || !password || loading ? DISABLED_BG : CORAL,
            color: !email || !password || loading ? MUTED : "#fff",
            border: "none",
            borderRadius: 22,
            fontFamily: "var(--font-body)",
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 500,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "…" : "Sign up with email"}
        </button>
        <button
          onClick={google}
          style={{
            width: "100%",
            minHeight: 44,
            padding: 12,
            background: CARD,
            border: `1px solid ${PILL_BORDER}`,
            borderRadius: 22,
            fontFamily: "var(--font-body)",
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 500,
            color: INK,
            cursor: "pointer",
          }}
        >
          Continue with Google
        </button>
        {!isAndroid() && (
          <button
            onClick={apple}
            style={{
              width: "100%",
              minHeight: 44,
              padding: 12,
              background: "#000",
              color: "#fff",
              border: "none",
              borderRadius: 22,
              fontFamily: "var(--font-body)",
              fontSize: 15,
              lineHeight: 1.4,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Continue with Apple
          </button>
        )}
        {error && (
          <p className="l2f-type-caption" style={{ color: CORAL }}>{error}</p>
        )}
      </div>
    </div>
  );
}

// ───────── Sizes content ─────────

function SizesContent({
  gender,
  answers,
  setAns,
}: {
  gender?: Gender;
  answers: Answers;
  setAns: (p: Partial<Answers>) => void;
}) {
  const topLetters = TOP_SIZE_OPTIONS;
  const ukNumbers = [6, 8, 10, 12, 14, 16, 18, 20];
  const waistInches = [28, 30, 32, 34, 36, 38, 40, 42];
  const womenShoe = [3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];
  const menShoe = [6, 7, 8, 9, 10, 11, 12, 13];
  const isMen = gender === "men";

  return (
    <div>
      <Eyebrow>LIKE2FIT</Eyebrow>
      <Headline>What sizes do you wear?</Headline>
      <Hint>Optional — helps us only ever show things you can actually buy.</Hint>

      <div style={{ marginTop: 14 }}>
        <SectionCard eyebrow={isMen ? "TOP / SHIRT" : "TOP SIZE"}>
          <div>
            {topLetters.map((l) => (
              <SmallPill
                key={l}
                label={l}
                selected={answers.sizeTopLetter === l}
                onClick={() => setAns({ sizeTopLetter: l })}
              />
            ))}
          </div>
        </SectionCard>

        {!isMen && (
          <SectionCard eyebrow="UK NUMBER (OPTIONAL)">
            <div>
              {ukNumbers.map((n) => (
                <SmallPill
                  key={n}
                  label={String(n)}
                  selected={answers.sizeTopUkNumber === n}
                  onClick={() =>
                    setAns({ sizeTopUkNumber: answers.sizeTopUkNumber === n ? undefined : n })
                  }
                />
              ))}
            </div>
          </SectionCard>
        )}

        {isMen ? (
          <>
            <SectionCard eyebrow="WAIST (INCHES)">
              <div>
                {waistInches.map((w) => (
                  <SmallPill
                    key={w}
                    label={String(w)}
                    selected={answers.sizeTrouserWaist === w}
                    onClick={() => setAns({ sizeTrouserWaist: w })}
                  />
                ))}
              </div>
            </SectionCard>
            <SectionCard eyebrow="SHOE SIZE (UK)">
              <div>
                {menShoe.map((s) => (
                  <SmallPill
                    key={s}
                    label={String(s)}
                    selected={answers.sizeShoeMens === s}
                    onClick={() => setAns({ sizeShoeMens: s })}
                  />
                ))}
              </div>
            </SectionCard>
          </>
        ) : (
          <>
            <SectionCard eyebrow="BOTTOM SIZE (UK)">
              <div>
                {ukNumbers.map((n) => (
                  <SmallPill
                    key={n}
                    label={String(n)}
                    selected={answers.sizeUkNumber === n}
                    onClick={() => setAns({ sizeUkNumber: n })}
                  />
                ))}
              </div>
            </SectionCard>
            <SectionCard eyebrow="SHOE SIZE (UK)">
              <div>
                {womenShoe.map((s) => (
                  <SmallPill
                    key={s}
                    label={String(s)}
                    selected={answers.sizeShoe === s}
                    onClick={() => setAns({ sizeShoe: s })}
                  />
                ))}
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
}

// ───────── Budget content ─────────

function BudgetContent({
  answers,
  setAns,
}: {
  answers: Answers;
  setAns: (p: Partial<Answers>) => void;
}) {
  const rows: { key: "budgetTop" | "budgetBottom" | "budgetShoes"; label: string }[] = [
    { key: "budgetTop", label: "TOPS" },
    { key: "budgetBottom", label: "BOTTOMS" },
    { key: "budgetShoes", label: "SHOES" },
  ];

  return (
    <div>
      <Eyebrow>LIKE2FIT</Eyebrow>
      <Headline>Comfortable budget per item?</Headline>
      <Hint>Optional — you can update this anytime.</Hint>

      <div style={{ marginTop: 14 }}>
        {rows.map((r) => {
          const v = answers[r.key];
          const noLimit = v === -1;
          return (
            <SectionCard key={r.key} eyebrow={r.label}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div
                  style={{
                    flex: 1,
                    background: noLimit ? DISABLED_INPUT_BG : CARD,
                    border: `1px solid ${PILL_BORDER}`,
                    borderRadius: 12,
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ color: MUTED, fontSize: 16, lineHeight: 1.5 }}>£</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={noLimit ? "" : v ?? ""}
                    disabled={noLimit}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, "");
                      setAns({
                        [r.key]: digits ? Number(digits) : undefined,
                      } as Partial<Answers>);
                    }}
                    placeholder=""
                    style={{
                      flex: 1,
                      border: "none",
                      background: "transparent",
                      outline: "none",
                      fontSize: 16,
                      color: noLimit ? MUTED : INK,
                      width: "100%",
                    }}
                  />
                </div>
                <SmallPill
                  label="No limit"
                  selected={noLimit}
                  onClick={() =>
                    setAns({ [r.key]: noLimit ? undefined : -1 } as Partial<Answers>)
                  }
                />
              </div>
            </SectionCard>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Reveal content ─────────

function RevealContent({
  answers,
  gender,
}: {
  answers: Answers;
  gender: Gender | null;
}) {
  const [revealComplete, setRevealComplete] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setRevealComplete(true), 3300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        paddingTop: "calc(var(--safe-top, 0px) + 24px)",
      }}
    >
      <div
        style={{
          background: CARD,
          borderRadius: 24,
          padding: 20,
          boxShadow: "0 12px 32px rgba(44,40,32,0.12)",
          width: "min(60vw, 220px)",
        }}
      >
        <div style={{ width: "100%", height: 240 }}>
          <SilhouetteModel
            gender={gender}
            height={answers.height ?? null}
            bodyShape={answers.bodyShape ?? null}
            bodySize={answers.bodySize ?? null}
            skinTone={answers.skinTone ?? null}
            hair={answers.hair ?? null}
            isReveal
            className="h-full w-full"
          />
        </div>
      </div>

      <div
        style={{
          opacity: revealComplete ? 1 : 0,
          transition: "opacity 0.6s ease",
          marginTop: 24,
          maxWidth: 320,
        }}
      >
        <Eyebrow>LIKE2FIT · MEET YOUR MODEL</Eyebrow>
        <Headline size={26}>There you are</Headline>
        <Body>
          Every outfit you analyse will be styled on this version of you.
        </Body>
      </div>
    </div>
  );
}

// ───────── Brand combined content ─────────

function BrandCombinedContent({
  gender,
  answers,
  setAns,
}: {
  gender: Gender | null;
  answers: Answers;
  setAns: (p: Partial<Answers>) => void;
}) {
  const isMen = gender === "men";
  const isNB = gender === "non-binary";

  const brands = isMen
    ? ["Zara", "ASOS", "H&M", "Next", "None"]
    : isNB
      ? ["Zara", "ASOS", "H&M", "COS", "None"]
      : ["Zara", "ASOS", "H&M", "& Other Stories", "None"];

  return (
    <div>
      <Eyebrow>LIKE2FIT</Eyebrow>
      <Headline>Brand fit quirks?</Headline>
      <Hint>Optional — helps us flag sizing issues before you buy.</Hint>

      <div style={{ marginTop: 14 }}>
        <BrandFitInlineNew
          eyebrow="TOPS"
          brands={brands}
          value={answers.brandFitTops}
          onChange={(v) => setAns({ brandFitTops: v })}
          category="tops"
        />
        <BrandFitInlineNew
          eyebrow="BOTTOMS"
          brands={brands}
          value={answers.brandFitBottoms}
          onChange={(v) => setAns({ brandFitBottoms: v })}
          category="bottoms"
        />
      </div>
    </div>
  );
}

function BrandFitInlineNew({
  eyebrow,
  brands,
  value,
  onChange,
  category,
}: {
  eyebrow: string;
  brands: string[];
  value?: BrandFit[];
  onChange: (v: BrandFit[]) => void;
  category: "tops" | "bottoms";
}) {
  const current = value?.[0];
  const isNone = value !== undefined && value.length === 0;
  const [selectedBrand, setSelectedBrand] = useState<string | null>(current?.brand ?? null);
  const [verdict, setVerdict] = useState<Verdict | null>(current?.verdict ?? null);
  const [none, setNone] = useState(isNone);

  useEffect(() => {
    if (none) onChange([]);
    else if (selectedBrand && verdict)
      onChange([{ brand: selectedBrand, verdict, category }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [none, selectedBrand, verdict]);

  return (
    <SectionCard eyebrow={eyebrow}>
      <p
        className="l2f-type-section-header"
        style={{ color: MUTED, marginBottom: 6 }}
      >
        BRAND
      </p>
      <div style={{ marginBottom: 8 }}>
        {brands.map((b) => {
          const isNoneBrand = b === "None";
          const active = isNoneBrand ? none : !none && selectedBrand === b;
          return (
            <SmallPill
              key={b}
              label={b}
              selected={active}
              onClick={() => {
                if (isNoneBrand) {
                  setNone(true);
                  setSelectedBrand(null);
                  setVerdict(null);
                } else {
                  setNone(false);
                  setSelectedBrand(b);
                }
              }}
            />
          );
        })}
      </div>
      {!none && selectedBrand && (
        <>
          <p
            className="l2f-type-section-header"
            style={{ color: MUTED, marginBottom: 6 }}
          >
            HOW DOES IT FIT?
          </p>
          <div>
            {(
              [
                ["runs_small", "Runs small"],
                ["true_to_size", "True to size"],
                ["runs_large", "Runs large"],
              ] as [Verdict, string][]
            ).map(([v, label]) => (
              <SmallPill
                key={v}
                label={label}
                selected={verdict === v}
                onClick={() => setVerdict(v)}
              />
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}

export default GrwmOnboarding;
